import React from "react";
import { Star } from "react-feather";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import { useShortcutIconSrc } from "../../../../hooks/useShortcutIconSrc";
import type { ProfileShortcut } from "../../../../types/profileShortcuts";

import styles from "./ProfileActionCard.module.css";

export interface ProfileActionCardProps {
  shortcut: ProfileShortcut;
}

/**
 * Illustrative badge count for the preview mock — fixed (not generated) so
 * every tile looks like the in-app profile without depending on real
 * unread/pending data.
 */
const PREVIEW_BADGE_COUNT = "3";

/**
 * Live-preview port of `trivelta-webapp`'s `ProfileActionCard`
 * (`src/app/profile-v2/components/profileActionCard/ProfileActionCard.tsx`)
 * — same 56px circular icon + 42px icon slot + 16px badge + label layout
 * (see `ProfileActionCard.module.css`'s header comment for the exact
 * mapping), used for the Profile "quick actions" row.
 *
 * Unlike the webapp original (which takes a resolved image URL + a real
 * per-action count), this reads straight off a `ProfileShortcut`: a missing
 * icon URL falls back to a placeholder glyph, otherwise the hosted asset
 * renders, and every live tile shows a hardcoded badge count as illustrative
 * demo chrome. Badge colour uses the CSS module default — the current
 * API/UI model has no per-shortcut notification colour field.
 */
const ProfileActionCard: React.FC<ProfileActionCardProps> = ({ shortcut }) => {
  const iconAlt = formatString(AppStrings.ICON_ALT, { name: shortcut.name });
  const { displaySrc, showImage, onImageError } = useShortcutIconSrc(shortcut);

  return (
    <div
      className={styles.container}
      data-testid={`preview-shortcut-${shortcut.id}`}
    >
      {/* `.iconInner img, .iconInner svg` in the CSS module fills this slot
          to 42x42 — mirrors the webapp's `'& img, & svg'` sx rule — so
          neither branch below needs its own explicit size. */}
      <div className={styles.iconWrapper}>
        <div className={styles.iconInner}>
          {showImage && displaySrc ? (
            <img
              src={displaySrc}
              alt={iconAlt}
              onError={onImageError}
            />
          ) : (
            <Star aria-hidden="true" />
          )}
        </div>
        <span
          className={styles.badge}
          data-testid={`preview-shortcut-${shortcut.id}-badge`}
        >
          {PREVIEW_BADGE_COUNT}
        </span>
      </div>
      <span className={styles.title}>{shortcut.name}</span>
    </div>
  );
};

export default ProfileActionCard;
